import { Modal, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

const { Text } = Typography;

interface InactivityWarningModalProps {
	open: boolean;
	secondsRemaining: number;
	onStaySignedIn: () => void;
	onSignOut: () => void;
}

const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
	open,
	secondsRemaining: initialSeconds,
	onStaySignedIn,
	onSignOut,
}) => {
	const [countdown, setCountdown] = useState(initialSeconds);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const onSignOutRef = useRef(onSignOut);
	onSignOutRef.current = onSignOut;

	useEffect(() => {
		if (open) {
			setCountdown(initialSeconds);
		}
	}, [open, initialSeconds]);

	useEffect(() => {
		if (!open) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		intervalRef.current = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(intervalRef.current!);
					intervalRef.current = null;
					onSignOutRef.current();
					return 0;
				}
				return prev - 1;
			});
		}, 1_000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [open]);

	const minutes = Math.floor(countdown / 60);
	const seconds = countdown % 60;
	const timeDisplay =
		minutes > 0
			? `${minutes}m ${seconds.toString().padStart(2, '0')}s`
			: `${seconds}s`;

	return (
		<Modal
			title="Session Expiring"
			open={open}
			onOk={onStaySignedIn}
			onCancel={onSignOut}
			okText="Stay signed in"
			cancelText="Sign out"
			centered
			closable={false}
			maskClosable={false}
		>
			<div style={{ textAlign: 'center', padding: '16px 0' }}>
				<Text>Your session will expire due to inactivity.</Text>
				<div style={{ fontSize: '24px', fontWeight: 'bold', margin: '16px 0' }}>
					{timeDisplay}
				</div>
				<Text type="secondary">
					Click &quot;Stay signed in&quot; to continue your session.
				</Text>
			</div>
		</Modal>
	);
};

export default InactivityWarningModal;
